import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Autocomplete,
  TextField,
  Input,
  Menu,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  SelectChangeEvent,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Tooltip,
  Popover,
} from "@mui/material";
import styled from "styled-components";
import { Form, useParams } from "react-router-dom";
import axios from "axios";
import { shallowEqual, useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../store";
import moment from "moment-timezone";
import { DateTime } from "luxon";
import { DatePicker } from "@mui/x-date-pickers";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { api, convertToCallablePhoneNumber, formatIsoToCustomDateStringWithEEEE } from "../utils/utils";
import { filterAppointmentTypesForPicker } from "../utils/appointmentTypes";
import { consolidateAvailableDates } from "../utils/availableDates";
import {
  ActivePlanOfCare,
  AppointmentType,
  AvailableDate,
  EHR,
  FrontendUser,
  OmniPatient,
  Organization,
  PartialOmniPatient,
  Patient,
  RawWebptPatient,
  WaitlistPatient,
  WebptPatient,
} from "../types";
import { PrimaryButton, SecondaryButton } from "../subcomponents/CustomButton";
import { HelpTooltip } from "../subcomponents/HelpTooltip";
import { DateRangePicker } from "@mui/x-date-pickers-pro";
import CloseIcon from "@mui/icons-material/Close";
import CircleIcon from "@mui/icons-material/Circle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { Colors } from "../Colors";
import { updatePatient } from "../slices/PatientSlice";
import LoadingWithMessage from "../subcomponents/LoadingWithMessage";
import CustomDatePicker from "../components/date-picker/DatePicker";
import Footer from "../subcomponents/Footer";
import { FooterBox, HeaderBox } from "../subcomponents/SidebarComponents";

import { v4 as uuidv4 } from "uuid";
import { dropdownStyles, menuPropsStyles } from "../styles/GeneralStyles";
import { first } from "lodash";
import { updatePatient as updatePatientThunk } from "../slices/PatientSlice";
import CustomAutocomplete from "../components/CustomAutocomplete";
import DatePickerField from "../subcomponents/DatePickerField";
import { AutoComplete } from "../subcomponents/AutoComplete";
import { useQueryClient } from "@tanstack/react-query";
import { patientPlansBriefQueryKey, usePatientPlansBrief } from "../hooks/usePatientPlansBrief";

const ModalBox = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 30%;
  margin: 10% auto;
  background-color: #fff;
  padding: 2%;
  border-radius: 15px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    width: 70%;
    padding: 5%;
  }
`;

const ModalScrollBox = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 30%;
  margin: 10% auto;
  background-color: #fff;
  padding: 2%;
  border-radius: 15px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  max-height: 70%;
  overflow-y: auto;

  @media (max-width: 768px) {
    width: 70%;
    padding: 5%;
  }
`;

const timezones = moment.tz.names();

const timezoneOptions = timezones.map((tz) => {
  const dt = DateTime.now().setZone(tz);
  const offsetString = dt.toFormat("ZZ");
  const text = `${tz} (UTC${offsetString})`;

  return { value: tz, label: text, offset: dt.offset };
});

timezoneOptions.sort((a, b) => a.offset - b.offset);

const contactOptions = [
  { value: "call", label: "Call" },
  { value: "text", label: "Text" },
];

const languageOptions = [
  { label: "English", value: "en" },
  /*
  { label: "Spanish", value: "es" },
  { label: "French", value: "fr" },
  { label: "German", value: "de" },
  { label: "Chinese", value: "zh" },
   */
  // TODO (Hizami): Figure out what languages we want
];

interface SettingsNumberInputProps {
  disabled: boolean;
  value: number | null;
  setter: (value: number | null) => void;
  label?: string;
}

const SettingsNumberInputLarge: React.FC<SettingsNumberInputProps> = ({ value, setter, label }) => {
  return (
    <Typography
      variant="body1"
      fontSize={"1rem"}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: "20px",
      }}
    >
      {label}:
      <TextField
        type="number"
        value={value}
        onChange={(e) => setter(parseInt(e.target.value))}
        variant="outlined"
        sx={{
          marginLeft: "8px",
          "& .MuiInputBase-input": {
            color: "#FFF",
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
            background: Colors.textfield,
          },
          "& .MuiOutlinedInput-input": {
            padding: "6px 15px",
          },
        }}
      />
    </Typography>
  );
};

const SettingsNumberInput: React.FC<SettingsNumberInputProps> = ({ disabled, value, setter }) => {
  return (
    <TextField
      type="number"
      value={value || ""}
      onChange={(e) => setter(parseInt(e.target.value))}
      variant="outlined"
      size="small"
      disabled={disabled}
      sx={{
        width: "45px",
        margin: "0 8px",
        "& .MuiInputBase-input": {
          color: "#FFF",
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: "8px",
          background: Colors.textfield,
        },
        "& .MuiOutlinedInput-input": {
          padding: "2px",
          textAlign: "center",
        },
        "& input[type=number]": {
          "-moz-appearance": "textfield", // Firefox
          "&::-webkit-outer-spin-button": {
            "-webkit-appearance": "none", // Chrome, Safari, Edge, Opera
            margin: 0,
          },
          "&::-webkit-inner-spin-button": {
            "-webkit-appearance": "none", // Chrome, Safari, Edge, Opera
            margin: 0,
          },
        },
      }}
    />
  );
};

interface WebptUpdatePatientModalProps {
  patient: OmniPatient;
  open: boolean;
  onClose: () => void;
  activePlansOfCare?: ActivePlanOfCare[];
  onProviderAssigned?: () => void;
}

export const UpdateWebptPatientModal: React.FC<WebptUpdatePatientModalProps> = ({ patient, open, onClose, activePlansOfCare, onProviderAssigned }) => {
  const dispatch = useAppDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const organization = useSelector((state: RootState) => state.auth.organization);
  const { agentId } = useParams<{ agentId: string }>();
  const [availableDates, setAvailableDates] = React.useState<AvailableDate[]>(patient.waitlist.availableDates);
  const [selectedProviders, setSelectedProviders] = React.useState<string[]>(patient[EHR.webpt]?.webptProviderIds || []);
  const [providerGroupId, setProviderGroupId] = React.useState<string | undefined>(patient[EHR.webpt]?.providerGroupId);
  const [loading, setLoading] = useState(false);
  const duration = patient[EHR.webpt]?.webptAppointmentDuration || 30;
  const [caseStudies, setCaseStudies] = useState<{ id: number; name: string; isActive: boolean }[]>([]); // Store case studies for the selected patient
  const [caseStudiesLoading, setCaseStudiesLoading] = useState(true);
  const [selectedCaseStudy, setSelectedCaseStudy] = React.useState<number | undefined>(patient[EHR.webpt]?.webptCaseStudyId);

  const [maxAppointmentsPerWeek, setMaxAppointmentsPerWeek] = React.useState<number | null>(patient.waitlist.maxAppointmentsPerWeek || null);
  const [gapBetweenAppointments, setGapBetweenAppointments] = React.useState<number | null>(patient.waitlist.gapBetweenAppointments || null);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [removalWindow, setRemovalWindow] = useState<number | null>(patient[EHR.webpt]?.removalWindow || null);
  const [mode, setMode] = useState<"smart" | "legacy">("smart");

  const providers = useSelector((state: RootState) => state.webptIntegration.providers);
  const providerOptions = providers.map((provider) => ({
    value: provider.id,
    label: provider.name,
  }));

  const providerGroups = useSelector((state: RootState) => state.providers.providerGroups);
  const providerGroupOptions = providerGroups.map((group) => ({
    value: group.providerGroupId,
    label: group.name,
  }));

  const providerNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    providers.forEach((p) => { map[p.id] = p.name; });
    return map;
  }, [providers]);

  const providerGroupNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    providerGroups.forEach((g) => { map[g.providerGroupId] = g.name; });
    return map;
  }, [providerGroups]);

  const [patientGroupId, setPatientGroupId] = React.useState<string | undefined>(patient.waitlist.patientGroupId ?? undefined);
  const patientGroupOptions = [
    { value: "", label: "None" },
    ...(organization?.patientGroups?.map((group) => ({
      value: group.patientGroupId,
      label: group.name,
    })) || []),
  ];

  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
  const [selectedAppointmentType, setSelectedAppointmentType] = useState<string | undefined>(patient[EHR.webpt]?.webptAppointmentTypeId);

  const queryClient = useQueryClient();
  const {
    data: fetchedPlansOfCare,
    isLoading: plansOfCareLoading,
    isFetching: plansOfCareFetching,
  } = usePatientPlansBrief({
    patientId: patient.patientId,
    token: user?.token,
    enabled: open,
  });
  const localActivePlansOfCare: ActivePlanOfCare[] = fetchedPlansOfCare ?? activePlansOfCare ?? [];
  // True until we have our own authoritative fetch back; used to avoid flashing
  // "missing" warnings for case studies that may actually have plans.
  const plansOfCareNotYetResolved = fetchedPlansOfCare === undefined && (plansOfCareLoading || plansOfCareFetching);

  const [providerPopoverAnchor, setProviderPopoverAnchor] = useState<HTMLElement | null>(null);
  const [editingPocId, setEditingPocId] = useState<string | null>(null);
  const [editProviderGroupId, setEditProviderGroupId] = useState<string>("");
  const [editSelectedProviders, setEditSelectedProviders] = useState<string[]>([]);
  const [savingProviders, setSavingProviders] = useState<boolean>(false);

  const openProviderPopover = (event: React.MouseEvent<HTMLElement>, planOfCare: ActivePlanOfCare) => {
    event.stopPropagation();
    setProviderPopoverAnchor(event.currentTarget);
    setEditingPocId(planOfCare.activePlanOfCareId);
    setEditProviderGroupId(planOfCare.providerGroupId || "");
    setEditSelectedProviders(planOfCare.webptProviderIds || []);
  };

  const closeProviderPopover = () => {
    setProviderPopoverAnchor(null);
    setEditingPocId(null);
  };

  const handleEditProviderGroupChange = (groupId: string) => {
    setEditProviderGroupId(groupId);
    if (groupId) {
      const group = providerGroups?.find((g) => g.providerGroupId === groupId);
      if (group) setEditSelectedProviders(group.providerIds);
    }
  };

  const saveProviderAssignment = async () => {
    if (!user?.token || !editingPocId) return;
    setSavingProviders(true);
    try {
      await api.patch(`/plan-of-care/active/${editingPocId}`, user.token, {
        webptProviderIds: editSelectedProviders,
        providerGroupId: editProviderGroupId || null,
      });
      queryClient.setQueryData<ActivePlanOfCare[]>(
        patientPlansBriefQueryKey(patient.patientId),
        (prev) =>
          (prev ?? []).map((poc) =>
            poc.activePlanOfCareId === editingPocId
              ? { ...poc, webptProviderIds: editSelectedProviders, providerGroupId: editProviderGroupId || undefined }
              : poc
          )
      );
      closeProviderPopover();
      onProviderAssigned?.();
    } catch (error) {
      console.error("Error updating provider assignment:", error);
    } finally {
      setSavingProviders(false);
    }
  };

  async function fetchCaseStudies() {
    if (user?.token && patient[EHR.webpt]?.webptPatientId) {
      setCaseStudiesLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/webpt/patients/${patient[EHR.webpt]?.webptPatientId}`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });
        const data = response.data;

        const studies = data.caseStudies;
        setCaseStudies(studies);
      } finally {
        setCaseStudiesLoading(false);
      }
    } else {
      setCaseStudiesLoading(false);
    }
  }

  useEffect(() => {
    fetchCaseStudies();
  }, [user?.token, patient[EHR.webpt]?.webptPatientId]);

  useEffect(() => {
    const fetchAppointmentTypes = async () => {
      if (!user?.token || !organization?.organizationId) {
        return;
      }
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/appointment-types`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
          params: {
            organizationId: organization.organizationId,
          },
        });
        setAppointmentTypes(response.data);
      } catch (error) {
        console.error("Error fetching appointment types:", error);
      }
    };
    fetchAppointmentTypes();
  }, [user?.token, organization?.organizationId]);

  const handleProviderGroupChange = (providerGroupId: string) => {
    setProviderGroupId(providerGroupId);
    if (providerGroupId) {
      const group = providerGroups?.find((group) => group.providerGroupId === providerGroupId);
      if (group) {
        setSelectedProviders(group.providerIds);
      }
    }
  };

  useEffect(() => {
    if (gapBetweenAppointments && gapBetweenAppointments > 2) {
      setGapBetweenAppointments(2);
    } else if (gapBetweenAppointments && gapBetweenAppointments < 0) {
      setGapBetweenAppointments(0);
    }
  }, [gapBetweenAppointments]);

  const updatePatient = async () => {
    if (!user?.token || !organization?.organizationId) {
      return;
    }
    if (!selectedCaseStudy) {
      return;
    }
    setLoading(true);

    try {
      const patientId = patient.patientId;
      const organizationId = organization.organizationId;

      const basePatient: Partial<Patient> = {
        patientId,
      };

      const waitlistPatient: Partial<WaitlistPatient> = {
        patientId,
        availableDates: availableDates,
        patientGroupId: patientGroupId || null,
        maxAppointmentsPerWeek: maxAppointmentsPerWeek === null ? undefined : maxAppointmentsPerWeek,
        gapBetweenAppointments: gapBetweenAppointments === null ? undefined : gapBetweenAppointments,
      };

      const webptPatient: Partial<WebptPatient> = {
        patientId,
        webptPatientId: patient[EHR.webpt]?.webptPatientId,
        providerGroupId: providerGroupId,
        webptProviderIds: selectedProviders,
        webptAppointmentDuration: duration,
        webptCaseStudyId: selectedCaseStudy,
        removalWindow: removalWindow || undefined,
        webptAppointmentTypeId: selectedAppointmentType,
      };

      const omniPatient: PartialOmniPatient = {
        patientId: patientId,
        patient: basePatient,
        waitlist: waitlistPatient,
        [EHR.webpt]: webptPatient,
      };

      await dispatch(
        updatePatientThunk({
          token: user.token,
          patient: omniPatient,
        })
      );
      onClose();
    } catch (error) {
      // Handle any errors from the thunk if needed
      console.error("Error creating patient:", error);
    }
    setLoading(false);
  };

  const caseStudyOptions = caseStudies.map((study) => {
    return {
      value: study.id,
      label: study.name,
      isActive: study.isActive,
    };
  });

  const updatePatientSmart = async () => {
    if (!user?.token || !organization?.organizationId) return;
    setLoading(true);
    try {
      const waitlistPatient: Partial<WaitlistPatient> = {
        patientId: patient.patientId,
        availableDates: consolidateAvailableDates(availableDates),
        patientGroupId: patientGroupId || null,
      };
      const omniPatient: PartialOmniPatient = {
        patientId: patient.patientId,
        patient: { patientId: patient.patientId },
        waitlist: waitlistPatient,
      };
      await dispatch(updatePatientThunk({ token: user.token, patient: omniPatient }));
      onClose();
    } catch (error) {
      console.error("Error updating patient:", error);
    }
    setLoading(false);
  };

  const patientBox = (
    <Box
      sx={{
        padding: "10px",
        background: Colors.textfield,
        borderRadius: "10px",
        color: "#FFFFFF",
        marginTop: "5px",
      }}
    >
      <Typography variant="body1" fontWeight={"bold"} fontSize={"1rem"}>
        {`${patient.patient.firstName ?? ""} ${patient.patient.lastName ?? ""}`}
      </Typography>
      <Box
        sx={{
          color: Colors.info,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "left", sm: "center" },
          gap: "10px",
        }}
        mt={0.5}
      >
        <Typography variant="body2">{`${patient.patient.phoneNumber}`}</Typography>
        <CircleIcon sx={{ fontSize: "5px", display: { xs: "none", sm: "block" } }} />
        <Typography variant="body2">{`ID: ${patient[EHR.webpt]?.webptPatientId ?? ""}`}</Typography>
        <CircleIcon sx={{ fontSize: "5px", display: { xs: "none", sm: "block" } }} />
        <Typography variant="body2">{`${patient.patient.timezone}`}</Typography>
      </Box>
    </Box>
  );

  const badgeStyles = {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "3px 8px",
    borderRadius: "5px",
    backgroundColor: "rgba(255, 255, 255, 0.06)",
  };

  return (
    <Box
      sx={{
        overflowY: "auto",
        height: "100%",
        paddingTop: "80px",
        paddingBottom: "100px",
        paddingRight: "20px",
      }}
    >
      <HeaderBox title="Manage Waitlist" onClose={onClose} />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
        <Typography variant="body1">Patient</Typography>
        {mode === "smart" && (
          <Typography
            variant="body2"
            onClick={() => setMode("legacy")}
            sx={{
              color: Colors.info,
              fontSize: "0.75rem",
              cursor: "pointer",
              textDecoration: "underline",
              textDecorationColor: "rgba(159, 161, 166, 0.3)",
              textUnderlineOffset: "3px",
              "&:hover": {
                color: "rgba(255,255,255,0.85)",
                textDecorationColor: "rgba(255,255,255,0.5)",
              },
            }}
          >
            Use legacy manual mode
          </Typography>
        )}
      </Box>
      {patientBox}

      {mode === "legacy" && (
        <Box
          sx={{
            mt: 3,
            padding: "12px 14px",
            borderRadius: "10px",
            border: `1px solid rgba(252, 213, 84, 0.3)`,
            backgroundColor: "rgba(252, 213, 84, 0.06)",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <WarningAmberIcon sx={{ fontSize: "18px", color: Colors.warning, flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ color: "#FFFFFF", fontWeight: 600, fontSize: "0.8125rem", lineHeight: 1.3 }}>
              Legacy Manual Mode
            </Typography>
            <Typography variant="body2" sx={{ color: Colors.info, fontSize: "0.75rem", mt: 0.25, lineHeight: 1.4 }}>
              This mode is deprecated. Plan of Care is the recommended way to manage the waitlist.
            </Typography>
          </Box>
          <Button
            onClick={() => setMode("smart")}
            size="small"
            variant="outlined"
            sx={{
              textTransform: "none",
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#FFFFFF",
              borderColor: "rgba(255,255,255,0.25)",
              whiteSpace: "nowrap",
              flexShrink: 0,
              "&:hover": {
                borderColor: "#FFFFFF",
                backgroundColor: "rgba(255,255,255,0.06)",
              },
            }}
          >
            Switch to Plan of Care
          </Button>
        </Box>
      )}

      {mode === "smart" && (
        <>
        <Box mt={3}>
          <Typography variant="body1" fontWeight="bold" mb={1}>
            Case Studies
          </Typography>

          {caseStudiesLoading || plansOfCareNotYetResolved ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, padding: "14px 16px", mt: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                Loading case studies…
              </Typography>
            </Box>
          ) : caseStudies.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px 14px",
                borderRadius: "10px",
                border: `1px solid rgba(${Colors.warning}, 0.3)`,
                backgroundColor: "rgba(252, 213, 84, 0.06)",
                mt: 1,
              }}
            >
              <WarningAmberIcon sx={{ fontSize: "16px", color: Colors.warning, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: Colors.warning }}>
                No case studies found for this patient.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", mt: 1 }}>
              {caseStudies.map((study) => {
                const planOfCare = localActivePlansOfCare?.find((poc) => poc.caseStudyId === study.id);
                const hasPoc = !!planOfCare;

                const providerIds = planOfCare?.webptProviderIds || [];
                const providerNames = providerIds.map((id) => providerNameMap[id]).filter(Boolean);
                const groupName = planOfCare?.providerGroupId ? providerGroupNameMap[planOfCare.providerGroupId] : null;
                const hasProviders = groupName || providerNames.length > 0;

                const authUsed = planOfCare?.authorizationUsed || 0;
                const authTotal = planOfCare?.authorizationTotal || 0;
                const hasAuthInfo = authTotal > 0 && !planOfCare?.ignoreAuthorization;
                const authPercentage = authTotal > 0 ? (authUsed / authTotal) * 100 : 0;
                const isAuthLow = authPercentage > 80;

                const frequencyText = planOfCare
                  ? planOfCare.frequency
                    ? `${planOfCare.frequency}x/week`
                    : `Every ${planOfCare.gap}d`
                  : null;

                return (
                  <Box
                    key={study.id}
                    sx={{
                      borderRadius: "10px",
                      border: "1px solid rgba(255, 255, 255, 0.12)",
                      backgroundColor: "rgba(255, 255, 255, 0.04)",
                      padding: "14px 16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    {/* Title row */}
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      fontSize="0.9375rem"
                      sx={{ color: study.isActive ? "rgba(255,255,255,0.95)" : Colors.info, lineHeight: 1.3 }}
                    >
                      {study.name}
                      {!study.isActive && (
                        <Typography component="span" variant="body2" sx={{ color: Colors.error, ml: 1, fontWeight: 400 }}>
                          (inactive)
                        </Typography>
                      )}
                    </Typography>

                    {/* POC details row */}
                    {hasPoc && (
                      <>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                          {/* Frequency */}
                          {frequencyText && (
                            <Box sx={badgeStyles}>
                              <CalendarTodayIcon sx={{ fontSize: "12px", color: "rgba(255,255,255,0.5)" }} />
                              <Typography variant="body2" fontSize="0.75rem" sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 500 }}>
                                {frequencyText}
                              </Typography>
                            </Box>
                          )}

                          {/* Authorization */}
                          {hasAuthInfo && (
                            <Box
                              sx={{
                                ...badgeStyles,
                                backgroundColor: isAuthLow ? "rgba(255, 152, 0, 0.1)" : "rgba(255,255,255,0.06)",
                                border: isAuthLow ? "1px solid rgba(255, 152, 0, 0.3)" : "none",
                              }}
                            >
                              <VerifiedUserIcon sx={{ fontSize: "12px", color: isAuthLow ? Colors.warning : "rgba(255,255,255,0.5)" }} />
                              <Typography
                                variant="body2"
                                fontSize="0.75rem"
                                sx={{ color: isAuthLow ? Colors.warning : "rgba(255,255,255,0.7)", fontWeight: 500 }}
                              >
                                {authUsed}/{authTotal} auth visits
                              </Typography>
                            </Box>
                          )}

                          {/* Providers — clickable to assign/reassign */}
                          {groupName ? (
                            <Box
                              sx={{ ...badgeStyles, cursor: "pointer", "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" } }}
                              onClick={(e) => openProviderPopover(e, planOfCare)}
                            >
                              <GroupIcon sx={{ fontSize: "12px", color: "rgba(255,255,255,0.5)" }} />
                              <Typography
                                variant="body2"
                                fontSize="0.75rem"
                                sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 500, maxWidth: "140px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                              >
                                {groupName}
                              </Typography>
                            </Box>
                          ) : providerNames.length > 0 ? (
                            <Box
                              sx={{ ...badgeStyles, cursor: "pointer", "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" } }}
                              onClick={(e) => openProviderPopover(e, planOfCare)}
                            >
                              <PersonIcon sx={{ fontSize: "12px", color: "rgba(255,255,255,0.5)" }} />
                              <Typography variant="body2" fontSize="0.75rem" sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 500 }}>
                                {providerNames.length === 1 ? providerNames[0] : `${providerNames.length} providers`}
                              </Typography>
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                ...badgeStyles,
                                backgroundColor: "rgba(255, 152, 0, 0.1)",
                                border: "1px dashed rgba(255, 152, 0, 0.5)",
                                cursor: "pointer",
                                "&:hover": { backgroundColor: "rgba(255, 152, 0, 0.18)" },
                              }}
                              onClick={(e) => openProviderPopover(e, planOfCare)}
                            >
                              <WarningAmberIcon sx={{ fontSize: "12px", color: Colors.warning }} />
                              <Typography variant="body2" fontSize="0.75rem" sx={{ color: Colors.warning, fontWeight: 500 }}>
                                + Assign provider
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Warning when no provider is assigned */}
                        {!hasProviders && (
                          <Typography variant="body2" fontSize="0.78rem" sx={{ color: Colors.warning }}>
                            Patient will not receive waitlist offerings until a provider is assigned.
                          </Typography>
                        )}
                      </>
                    )}

                    {/* No POC warning message */}
                    {!hasPoc && (
                      <Typography variant="body2" fontSize="0.8rem" sx={{ color: Colors.warning }}>
                        Waitlist will not automatically detect unscheduled follow-ups from the plan of care because no plan of care has been created for this case.
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {(organization?.patientGroups?.length || 0) > 0 && (
          <Box mt={3}>
            <Typography variant="body1" fontWeight="bold" mb={0.5}>
              Priority Group
            </Typography>
            <Typography variant="body2" sx={{ color: Colors.info, mb: 1 }}>
              Patients in higher-priority groups are offered waitlist slots first.
            </Typography>
            <FormControl fullWidth>
              <Select
                value={patientGroupId ?? ""}
                onChange={(e) => setPatientGroupId(e.target.value)}
                sx={{
                  borderRadius: "10px",
                  background: Colors.textfield,
                  "& .MuiInputBase-input": {
                    padding: "15px",
                    fontSize: "0.9rem",
                    color: "#FFFFFF",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#FFF",
                  },
                }}
              >
                {patientGroupOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <Box mt={3} mb={1}>
          <Typography variant="body1" fontWeight="bold" mb={0.5}>
            Available Dates
          </Typography>
          <Typography variant="body2" sx={{ color: Colors.info, mb: 2 }}>
            Determines when the patient will be contacted about open slots.
          </Typography>
          <DatePickerField dates={availableDates} onDatesChange={setAvailableDates} placeholder="Select available dates" />
        </Box>

          <FooterBox onClick={updatePatientSmart} disabled={false} buttonText="Save" loading={loading} />

          {/* Provider Assignment Popover */}
          <Popover
            open={Boolean(providerPopoverAnchor)}
            anchorEl={providerPopoverAnchor}
            onClose={closeProviderPopover}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: Colors.bg3,
                  border: `1px solid ${Colors.border}`,
                  borderRadius: "12px",
                  width: "320px",
                  mt: 0.5,
                },
              },
            }}
          >
            <Box sx={{ p: 2.5 }}>
              <Typography variant="body2" fontWeight="600" sx={{ color: "#FFF", fontSize: "13px", mb: 2 }}>
                Assign Providers
              </Typography>

              {providerGroupOptions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "11px", display: "block", mb: 0.5 }}>
                    Provider Group
                  </Typography>
                  <Select
                    value={editProviderGroupId}
                    onChange={(e) => handleEditProviderGroupChange(e.target.value)}
                    displayEmpty
                    fullWidth
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      borderRadius: "8px",
                      color: "#FFF",
                      fontSize: "13px",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: Colors.border },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: Colors.border },
                    }}
                  >
                    <MenuItem value=""><em style={{ color: "rgba(255, 255, 255, 0.5)" }}>None</em></MenuItem>
                    {providerGroupOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </Box>
              )}

              {providerOptions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "11px" }}>
                      Providers
                    </Typography>
                    {!editProviderGroupId && (
                      editSelectedProviders.length === providerOptions.length ? (
                        <Typography
                          variant="caption"
                          onClick={() => setEditSelectedProviders([])}
                          sx={{ color: "rgba(255, 255, 255, 0.4)", fontSize: "10px", cursor: "pointer", "&:hover": { color: "#FFF" } }}
                        >
                          Deselect all
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          onClick={() => setEditSelectedProviders(providerOptions.map((o) => o.value))}
                          sx={{ color: "rgba(255, 255, 255, 0.4)", fontSize: "10px", cursor: "pointer", "&:hover": { color: "#FFF" } }}
                        >
                          Select all
                        </Typography>
                      )
                    )}
                  </Box>
                  <Select
                    disabled={!!editProviderGroupId}
                    multiple
                    displayEmpty
                    value={editSelectedProviders}
                    onChange={(e) => setEditSelectedProviders(e.target.value as string[])}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return <Typography sx={{ color: "rgba(255, 255, 255, 0.4)", fontSize: "13px" }}>Select providers</Typography>;
                      }
                      return (selected as string[]).map((v) => providerOptions.find((o) => o.value === v)?.label).filter(Boolean).join(", ");
                    }}
                    fullWidth
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      borderRadius: "8px",
                      color: "#FFF",
                      fontSize: "13px",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: Colors.border },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: Colors.border },
                    }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
                  >
                    {providerOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Checkbox checked={editSelectedProviders.indexOf(opt.value) > -1} size="small" />
                        <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: "13px" }} />
                      </MenuItem>
                    ))}
                  </Select>
                  {editProviderGroupId && (
                    <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.35)", fontSize: "10px", mt: 0.5, display: "block" }}>
                      Managed by provider group
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button
                  onClick={closeProviderPopover}
                  size="small"
                  sx={{ textTransform: "none", color: "rgba(255, 255, 255, 0.6)", fontSize: "12px", fontWeight: 500, minWidth: "auto", px: 1.5 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={saveProviderAssignment}
                  disabled={savingProviders}
                  size="small"
                  sx={{ textTransform: "none", fontSize: "12px", fontWeight: 600, minWidth: "auto", px: 2 }}
                >
                  {savingProviders ? "Saving..." : "Save"}
                </Button>
              </Box>
            </Box>
          </Popover>
        </>
      )}

      {mode === "legacy" && (
        <>
          {caseStudies.length > 0 && (
        <FormControl fullWidth>
          <Typography variant="body1" mt={3} mb={1}>
            Case Study
          </Typography>
          <Select
            value={selectedCaseStudy}
            onChange={(e) => setSelectedCaseStudy(Number(e.target.value))}
            sx={{
              borderRadius: "10px",
              background: Colors.textfield,
              marginTop: "5px",
              "& .MuiInputBase-input": {
                padding: "15px",
                fontSize: "0.9rem",
                color: "#FFFFFF",
              },
              "& .MuiSvgIcon-root": {
                color: "#FFF",
              },
            }}
          >
            {caseStudyOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.isActive ? (
                  <ListItemText primary={option.label} />
                ) : (
                  <ListItemText primary={`${option.label} (inactive)`} primaryTypographyProps={{ color: Colors.error }} />
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Typography variant="body1" fontWeight={"bold"} mt={3}>
        Waitlist Parameters <HelpTooltip title={"The patient will automatically be put on waitlists for appointments fitting these criteria."} />
      </Typography>

      {patientGroupOptions.length > 0 && (
        <FormControl fullWidth>
          <Box display="flex" alignItems="center" gap="10px" mt={2}>
            <Typography variant="body1">Priority Group</Typography>
          </Box>
          <Select
            value={patientGroupId}
            onChange={(e) => setPatientGroupId(e.target.value)}
            sx={{
              borderRadius: "10px",
              background: Colors.textfield,
              marginTop: "5px",
              "& .MuiInputBase-input": {
                padding: "15px",
                fontSize: "0.9rem",
                color: "#FFFFFF",
              },
              "& .MuiSvgIcon-root": {
                color: "#FFF",
              },
            }}
          >
            {patientGroupOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {providerGroupOptions.length > 0 && (
        <FormControl fullWidth>
          <Box display="flex" alignItems="center" gap="10px" mt={2}>
            <Typography variant="body1">Provider Group</Typography>
          </Box>
          <Select
            value={providerGroupId || ""}
            onChange={(e) => handleProviderGroupChange(e.target.value)}
            sx={{
              borderRadius: "10px",
              background: Colors.textfield,
              marginTop: "5px",
              "& .MuiInputBase-input": {
                padding: "15px",
                fontSize: "0.9rem",
                color: "#FFFFFF",
              },
              "& .MuiSvgIcon-root": {
                color: "#FFF",
              },
            }}
          >
            <MenuItem value="">
              <ListItemText primary="None" />
            </MenuItem>
            {providerGroupOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <FormControl fullWidth>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px">
          <Typography variant="body1">Providers</Typography>
          {selectedProviders.length === providerOptions.length ? (
            <Button sx={{ textTransform: "none" }} onClick={() => setSelectedProviders([])}>
              Deselect All
            </Button>
          ) : (
            <Button sx={{ textTransform: "none" }} onClick={() => setSelectedProviders(providerOptions.map((option) => option.value))}>
              Select All
            </Button>
          )}
        </Box>

        <Select
          disabled={!!providerGroupId}
          multiple
          displayEmpty
          value={selectedProviders}
          onChange={(e) => setSelectedProviders(e.target.value as string[])}
          renderValue={(selected) => {
            if (selected.length === 0) {
              return <Typography sx={{ color: Colors.info }}>Select options</Typography>;
            }
            return (selected as string[]).map((value) => providerOptions.find((option) => option.value === value)?.label).join(", ");
          }}
          sx={dropdownStyles}
          MenuProps={menuPropsStyles}
        >
          {providerOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox checked={selectedProviders.indexOf(option.value) > -1} sx={{ color: "#FFF" }} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {appointmentTypes.length > 0 && (
        <FormControl fullWidth>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px" mt={2}>
            <Tooltip title="This field is required">
              <Typography variant="body1">
                Appointment Types <span style={{ color: Colors.error }}>*</span>
              </Typography>
            </Tooltip>
          </Box>

          <Select
            displayEmpty
            value={selectedAppointmentType}
            onChange={(e) => setSelectedAppointmentType(e.target.value as string)}
            renderValue={(selected) => {
              if (!selected) {
                return <Typography sx={{ color: Colors.info }}>Select appointment type</Typography>;
              }
              const option = appointmentTypes.find((type) => type.appointmentTypeId === selected);
              return option ? option.name : "Unknown Appointment Type";
            }}
            sx={{
              borderRadius: "10px",
              background: Colors.textfield,
              marginTop: "5px",
              "& .MuiInputBase-input": {
                padding: "15px",
                fontSize: "0.9rem",
                color: "#FFFFFF",
              },
              "& .MuiSvgIcon-root": {
                color: "#FFF",
              },
            }}
            MenuProps={menuPropsStyles}
          >
            {filterAppointmentTypesForPicker(appointmentTypes, selectedAppointmentType, organization?.organizationId).map((type) => (
              <MenuItem key={type.appointmentTypeId} value={type.appointmentTypeId}>
                <ListItemText primary={type.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <SettingsNumberInputLarge
        disabled={false}
        value={maxAppointmentsPerWeek}
        setter={setMaxAppointmentsPerWeek}
        label="Max appointments per week"
      />

      <SettingsNumberInputLarge disabled={false} value={gapBetweenAppointments} setter={setGapBetweenAppointments} label="Gap between appointments" />

      <Box
        sx={{
          paddingBottom: "20px",
          paddingTop: "20px",
        }}
      >
        <DatePickerField dates={availableDates} onDatesChange={setAvailableDates} placeholder="Select dates" />
      </Box>

      <Button sx={{ textTransform: "none", marginTop: "20px" }} onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}>
        {showAdvancedOptions ? "Hide advanced options" : "Show advanced options"}
      </Button>

      {showAdvancedOptions && (
        <Box ml={2}>
          <Typography variant="body1">
            Remove patient from waitlist
            <SettingsNumberInput disabled={false} value={removalWindow} setter={setRemovalWindow} />
            surrounding days after appointment is booked.
          </Typography>
        </Box>
      )}

          <FooterBox onClick={updatePatient} disabled={false} buttonText="Update Waitlist Status" loading={loading} />
        </>
      )}
    </Box>
  );
};
interface ButtonRowProps {
  selectedValue: string;
  onChange: (value: any) => void;
  options: { label: string; value: string }[];
}

const ButtonRow: React.FC<ButtonRowProps> = ({ selectedValue, onChange, options }) => {
  return (
    <FormControl variant="outlined" fullWidth>
      <Box display="flex" gap="10px">
        {options.map((option) => (
          <Button
            key={option.value}
            onClick={() => onChange(option.value)}
            variant="outlined"
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              borderColor: selectedValue === option.value ? Colors.primary : "rgba(255, 255, 255, 0.2)",
              backgroundColor: "transparent",
              color: selectedValue === option.value ? "#fff" : Colors.info,
            }}
          >
            {option.label}
          </Button>
        ))}
      </Box>
    </FormControl>
  );
};
